import { useInfiniteQueryMemberTimeline } from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { useClubSettings } from "apps/portal/src/providers";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import useGymflowModels from "../../../store";
import { Spinner } from "../../atoms";
import EmailItem from "./EmailItem";
import LeadStatusItem from "./LeadStatusItem";
import MembershipStatusItem from "./MembershipStatusItem";
import TaskItem from "./TaskItem";
import TransactionItem from "./TransactionItem";

export const transactionTypeMap = {
  CREDIT: "Successful Payment",
  MANUAL_CREDIT: "Credit Applied",
  REFUND_DEBIT: "Refund",
  WRITE_OFF: "Debt Written off",
} as const;

const Filter = ({
  defaultValues,
  options = [],
  onChange,
}: {
  defaultValues: NodeType[];
  options: { label: string; value: NodeType }[];
  onChange: (v: NodeType[]) => void;
}) => {
  const [checked, setChecked] = useState(defaultValues);

  return (
    <div>
      {options.map((item, idx) => {
        const isChecked =
          checked.some((i) => i === item.value) ||
          checked.length + 1 === options.length;
        const isLastOne = checked.length === 1 && checked[0] === item.value;
        return (
          <span
            key={idx}
            className="h3 mr-3"
            style={{
              cursor: isLastOne ? "not-allowed" : "pointer",
              borderBottom: isChecked ? "1px solid #6186db" : "none",
              color: isChecked ? "#6186db" : "#9d9ea2",
            }}
            onClick={() => {
              if (!onChange || isLastOne) {
                return;
              }
              if (item.value === null) {
                const newValue = options
                  .map((o) => o.value)
                  .filter((o) => o !== null);
                onChange(newValue);
                setChecked(newValue);
              } else {
                if (isChecked) {
                  const newValue = checked.filter((o) => o !== item.value);
                  onChange(newValue);
                  setChecked(newValue);
                } else {
                  const newValue = [...checked, item.value];
                  onChange(newValue);
                  setChecked(newValue);
                }
              }
            }}
          >
            {item.label}
          </span>
        );
      })}
    </div>
  );
};

const timelineNodeMap = {
  EMAIL: EmailItem,
  LEADSTATUS: LeadStatusItem,
  SUBSCRIPTION: MembershipStatusItem,
  TASK: TaskItem,
  TRANSACTION: TransactionItem,
};

const nodeTypeOptions = [
  { label: "ALL", value: null as NodeType },
  { label: "EMAILS", value: "EMAIL" as NodeType },
  { label: "LEAD STATUS", value: "LEADSTATUS" as NodeType },
  { label: "MEMBERSHIP STATUS", value: "SUBSCRIPTION" as NodeType },
  { label: "TASKS", value: "TASK" as NodeType },
  { label: "TRANSACTIONS", value: "TRANSACTION" as NodeType },
];

const defaultFilter = nodeTypeOptions
  .filter((op) => !!op.value)
  .map((op) => op.value) as NodeType[];
const Timeline = ({
  fetchNodes,
  refreshNodes,
  value,
  isLast,
  isLoading,
  isFetching,
}: {
  fetchNodes: () => void;
  refreshNodes: (v: { nodeType: NodeType[] }) => void;
  value: any[];
  isLast: boolean;
  isLoading: boolean;
  isFetching: boolean;
}) => {
  const {
     properties, club ,
  } = useClubSettings();
  const dateFormat = properties.dateFormat;
  const nodes = value
    .filter(
      (node) =>
        !(
          node.nodeType === "TRANSACTION" &&
          !transactionTypeMap[node.type as keyof typeof transactionTypeMap]
        ),
    )
    .map((node) => {
      const Component =
        timelineNodeMap[node.nodeType as keyof typeof timelineNodeMap];
      if (Component) {
        return (
          <li key={node.nodeType + "_" + node.id}>
            <Component
              {...node}
              dateFormat={dateFormat}
              currency={club.defaultCurrency}
              refreshNodes={refreshNodes}
            />
          </li>
        );
      }

      return null;
    });
  useEffect(() => {
    if (nodes.length < 25 && !isFetching) {
      fetchNodes();
    }
  }, [nodes.length, isFetching, fetchNodes]);
  return (
    <div className="relative flex size-full">
      <div
        id="scrollableTimeline"
        className="absolute inset-0 flex flex-col gap-4 overflow-x-hidden"
      >
        <Filter
          defaultValues={defaultFilter}
          options={nodeTypeOptions}
          onChange={(v) => {
            refreshNodes({ nodeType: v as NodeType[] });
          }}
        />

        <Spinner className={cn("text-gray-300", { hidden: !isLoading })} />
        {!isLoading && value.length === 0 && (
          <p className={cn("text-muted", { hidden: value.length > 0 })}>
            No activity yet.
          </p>
        )}
        {!isLoading && nodes.length > 0 && (
          <InfiniteScroll
            className={cn({ hidden: nodes.length === 0 })}
            dataLength={nodes.length}
            hasMore={!isLast}
            hasChildren
            next={() => {
              fetchNodes();
            }}
            scrollableTarget="scrollableTimeline"
            loader={<Spinner className="text-gray-300" />}
          >
            <ul className="timeline">{nodes}</ul>
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};
type NodeType =
  | "EMAIL"
  | "LEADSTATUS"
  | "SUBSCRIPTION"
  | "TASK"
  | "TRANSACTION"
  | null;
const TimelineWrapper = ({
  memberId,
  leadId,
}: {
  memberId?: string;
  leadId?: number;
}) => {
  const { api } = useGymflowModels();
  const { club } = useClubSettings();
  const [timelineOpts, setTimelineOpts] = useState<{ nodeType: NodeType[] }>({
    nodeType: ["EMAIL", "LEADSTATUS", "SUBSCRIPTION", "TASK", "TRANSACTION"],
  });
  const {
    data: timeline,
    isLoading: isTimelineLoading,
    isFetching: isTimelineFetching,
    hasNextPage: hasMoreNodes,
    fetchNextPage: fetchMoreNodes,
  } = useInfiniteQueryMemberTimeline({
    api,
    tz: club.timezone,
    opts: {
      extraParams: {
        nodeType: timelineOpts?.nodeType,
        ...(memberId ? { userMemberId: memberId } : { leadId: leadId }),
      },
    },
  });
  const nodes = timeline?.pages.flatMap((p) => p.content) ?? [];
  return (
    <Timeline
      refreshNodes={({ nodeType }: { nodeType: NodeType[] }) => {
        setTimelineOpts({
          nodeType,
        });
      }}
      isLoading={isTimelineLoading}
      isFetching={isTimelineFetching}
      fetchNodes={fetchMoreNodes}
      value={nodes}
      isLast={!hasMoreNodes}
    />
  );
};
export default TimelineWrapper;
